<template>
  <div class="animated fadeIn">
    <b-card no-body class="border-0 shadow-sm mb-4 bg-dark">
      <div class="card-header bg-dark border-bottom-dark d-flex justify-content-between align-items-center py-3">
        <div class="d-flex align-items-center">
          <div class="bg-primary-soft-dark p-2 rounded mr-3">
            <i class="fa fa-comments text-primary"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold text-white">Chat Logs</h5>
            <small class="text-muted">Monitor communications across servers</small>
          </div>
        </div>

        <div class="d-flex align-items-center">
          <b-button-group size="sm" class="mr-3 shadow-sm">
            <b-button v-for="(item, index) in activeServers"
                      :key="index"
                      @click="switchServer(index)"
                      :variant="activeServer === index ? 'primary' : 'outline-dark'"
                      class="px-3 border-secondary text-light">
              <i class="fa fa-server mr-1 small"></i> {{ capitalizeFirstLetter(index) }}
            </b-button>
          </b-button-group>

          <b-button variant="outline-primary" size="sm" @click="refreshGrid" v-b-tooltip.hover title="Refresh Logs">
            <i class="fa fa-refresh" :class="{'fa-spin': loading}"></i>
          </b-button>
        </div>
      </div>

      <div class="chat-type-tabs border-bottom-dark">
        <div class="chat-type-tabs-inner">
          <div class="chat-type-tabs-search">
            <i class="fa fa-search"></i>
            <input
              v-model="messageQuickFilter"
              type="text"
              class="chat-type-tabs-input"
              placeholder="Filter message..."
              @input="applyMessageQuickFilter" />
          </div>
          <div class="chat-type-tabs-buttons">
            <button
              v-for="tab in chatTypeTabs"
              :key="tab.label"
              type="button"
              class="chat-type-tab"
              :class="[tab.value ? `chat-type-tab-${tab.value.toLowerCase()}` : '', { active: chatTypeFilter === tab.value }]"
              @click="setChatTypeFilter(tab.value)">
              <i v-if="tab.icon" :class="tab.icon"></i>
              <span>{{ tab.label }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="card-body p-0 position-relative bg-dark" ref="gridContainer">
        <div v-if="loading" class="grid-overlay d-flex justify-content-center align-items-center">
          <b-spinner variant="primary"></b-spinner>
        </div>

        <!-- Ghost Target for Popover Positioning -->
        <div id="popover-ghost-target"></div>

        <ag-grid-vue
          style="width: 100%; height: calc(100vh - 250px)"
          class="ag-theme-balham-dark custom-chat-grid"
          :gridOptions="gridOptions"
          @grid-ready="onGridReady">
        </ag-grid-vue>
      </div>
    </b-card>

    <!-- Manual Floating Pokemon Card -->
    <div v-show="hoveredPokemon.show"
         class="floating-pokemon-card-wrapper"
         :style="{ top: hoveredPokemon.y + 'px', left: hoveredPokemon.x + 'px' }">
      <pokemon-card v-if="hoveredPokemon.stats"
                    :statsString="hoveredPokemon.stats"
                    :name="hoveredPokemon.name" />
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import 'ag-grid-enterprise'
import Servers from '../../_servers'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { DefaultServerGridOptions } from "@/default_table_settings"
import { applyGridPersistence } from "@/grid_persistence"
import PokemonCard from './PokemonCard.vue'

export default {
  name: 'ChatLog',
  components: {
    'ag-grid-vue': AgGridVue,
    PokemonCard
  },
  data() {
    return {
      gridOptions: null,
      loading: false,
      activeServer: Object.keys(Servers).find(k => !Servers[k].readonly) || Object.keys(Servers)[0],
      servers: Servers,
      cb: null,
      chatTypeFilter: null,
      chatTypeTabs: [
        { label: 'Any', value: null, icon: 'fa fa-th-large' },
        { label: 'All', value: 'All', icon: 'fa fa-globe' },
        { label: 'Trade', value: 'Trade', icon: 'fa fa-exchange' },
        { label: 'Battle', value: 'Battle', icon: 'fa fa-bolt' },
        { label: 'Other', value: 'Other', icon: 'fa fa-ellipsis-h' },
        { label: 'Help', value: 'Help', icon: 'fa fa-life-ring' }
      ],
      messageQuickFilter: '',
      messageQuickFilterTimer: null,
      hoveredPokemon: {
        stats: null,
        name: null,
        show: false,
        x: 0,
        y: 0
      }
    }
  },
  computed: {
    ...mapGetters({
      chatlogs: 'Chatlogs',
      authUser: 'authUser',
      formMapping: 'formMapping'
    }),
    activeServers() {
      const filtered = {};
      for (const key in this.servers) {
        if (!this.servers[key].readonly) filtered[key] = this.servers[key];
      }
      return filtered;
    }
  },
  methods: {
    onGridReady(params) {
      params.api.setServerSideDatasource(this.createDatasource());
    },
    handleCellMouseOver(params) {
      if (!params || !params.event || !params.event.target) return;
      const link = params.event.target.closest ? params.event.target.closest('.pokemon-link') : null;
      if (link) {
        this.showPokemonCard(link);
      } else {
        this.hoveredPokemon.show = false;
      }
    },
    handleCellMouseOut() {
      this.hoveredPokemon.show = false;
    },
    handleGridMouseMove(event) {
      const link = event && event.target && event.target.closest ? event.target.closest('.pokemon-link') : null;
      if (link) {
        this.showPokemonCard(link);
      } else {
        this.hoveredPokemon.show = false;
      }
    },
    handleGridMouseLeave() {
      this.hoveredPokemon.show = false;
    },
    buildChatMessageCell(params) {
      const container = document.createElement('span');
      container.className = 'chat-message-text';
      container.innerHTML = this.parseChatMessage(params.value);

      container.addEventListener('mousemove', event => {
        const link = event.target && event.target.closest ? event.target.closest('.pokemon-link') : null;
        if (link) {
          this.showPokemonCard(link);
        } else {
          this.hoveredPokemon.show = false;
        }
      });

      container.addEventListener('mouseleave', () => {
        this.hoveredPokemon.show = false;
      });

      return container;
    },
    showPokemonCard(link) {
      const rect = link.getBoundingClientRect();

      // Position logic: prefer right of the link, but flip to left if near edge
      let posX = rect.right + 15;
      let posY = rect.top - 50; // Center it a bit vertically relative to link

      // Basic viewport collision detection
      if (posX + 300 > window.innerWidth) {
        posX = rect.left - 300;
      }
      if (posY + 400 > window.innerHeight) {
        posY = window.innerHeight - 410;
      }
      if (posY < 10) posY = 10;

      this.hoveredPokemon.x = posX;
      this.hoveredPokemon.y = posY;
      this.hoveredPokemon.stats = link.getAttribute('data-stats');
      this.hoveredPokemon.name = link.getAttribute('data-name');
      this.hoveredPokemon.show = true;
    },
    refreshGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        this.gridOptions.api.setServerSideDatasource(this.createDatasource());
      }
    },
    switchServer(index) {
      this.activeServer = index;
      this.refreshGrid();
    },
    setChatTypeFilter(value) {
      this.chatTypeFilter = value;
      if (!this.gridOptions || !this.gridOptions.api) return;
      const model = this.gridOptions.api.getFilterModel() || {};
      if (!value) {
        delete model.chat;
      } else {
        model.chat = { filterType: 'set', values: [value] };
      }
      this.gridOptions.api.setFilterModel(model);
      this.gridOptions.api.onFilterChanged();
    },
    applyMessageQuickFilter() {
      if (!this.gridOptions || !this.gridOptions.api) return;
      if (this.messageQuickFilterTimer) {
        clearTimeout(this.messageQuickFilterTimer);
        this.messageQuickFilterTimer = null;
      }
      this.messageQuickFilterTimer = setTimeout(() => {
        const model = this.gridOptions.api.getFilterModel() || {};
        const value = (this.messageQuickFilter || '').trim();
        if (!value) {
          delete model.message;
        } else {
          model.message = { filterType: 'text', type: 'contains', filter: value };
        }
        this.gridOptions.api.setFilterModel(model);
        this.gridOptions.api.onFilterChanged();
      }, 250);
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },
    buildUserCell(params) {
      const username = params.value || '';
      const link = document.createElement('a');
      link.className = 'chat-user-link text-info';
      link.textContent = username;
      link.href = this.userProfileUrl(username);
      link.addEventListener('click', event => {
        event.preventDefault();
        if (username) {
          this.$router.push({ path: `/user/${encodeURIComponent(username)}` });
        }
      });
      return link;
    },
    userProfileUrl(username) {
      const safe = encodeURIComponent(username || '');
      if (this.$router && this.$router.mode === 'hash') {
        return `/#/user/${safe}`;
      }
      return `/user/${safe}`;
    },
    createColumnDefs() {
      return [
        {
          headerName: 'Type',
          field: 'chat',
          width: 120,
          sortable: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: ['All', 'Trade', 'Battle', 'Other', 'Help']
          },
          cellRenderer: params => {
            if (!params.value) return '';
            const type = params.value.toLowerCase();
            let badgeStyle = 'background:#3a3f44;color:#f0f3f5;';
            if (type.includes('battle')) badgeStyle = 'background:#d84a4a;color:#fff;';
            if (type.includes('help')) badgeStyle = 'background:#2f80ed;color:#fff;';
            if (type.includes('trade')) badgeStyle = 'background:#f2994a;color:#1f2428;';
            if (type.includes('all')) badgeStyle = 'background:#20a8d8;color:#0b1a1f;';
            if (type.includes('other')) badgeStyle = 'background:#6c5ce7;color:#f5f5ff;';

            return `<span class="badge px-2 py-1 text-uppercase" style="font-size: 10px; min-width: 60px; ${badgeStyle}">${params.value}</span>`;
          }
        },
        {
          headerName: 'User',
          field: 'username',
          width: 150,
          filter: 'agTextColumnFilter',
          filterParams: { filterOptions: ['equals'], suppressAndOrCondition: true },
          sortable: false,
          cellRenderer: params => this.buildUserCell(params)
        },
        {
          headerName: 'Message',
          field: 'message',
          flex: 1,
          sortable: false,
          filter: 'agTextColumnFilter',
          filterParams: { filterOptions: ['equals', 'contains'], suppressAndOrCondition: true },
          cellRenderer: params => this.buildChatMessageCell(params),
          autoHeight: true
        },
        {
          headerName: 'Timestamp',
          field: 'date',
          width: 180,
          sort: 'desc',
          filter: 'agDateColumnFilter',
          cellRenderer: params => {
            if (!params.value) return '';
            const m = moment(params.value);
            return `<span class="text-muted"><i class="fa fa-clock-o mr-1"></i>${m.format('HH:mm:ss')}</span> <small class="ml-1 text-secondary">${m.format('DD.MM.YY')}</small>`;
          },
          tooltipValueGetter: params => {
            if (!params.value) return '';
            return moment(params.value).format('YYYY-MM-DD HH:mm:ss');
          }
        }
      ]
    },
    parseChatMessage(msg) {
      if (!msg) return '';

      let html = msg
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");

      // 1. Resolve Pokemon Links: [stats] [COLOR]<NAME>[-]
      const pokemonRegex = /\[([^\]]+)\]\s*(?:\[([A-Fa-f0-9]{6})\])?&lt;(.*?)&gt;(?:\[-\])?/g;
      html = html.replace(pokemonRegex, (match, statsStr, color, name) => {
        const s = statsStr.split(',');
        if (s.length < 27 || !/^\d+$/.test(s[0])) return match;
        const isShiny = s[2] === '1';
        const formId = Number(s[19] || 0);
        const formClass = formId > 0 ? ' pokemon-link-form' : '';
        const shinyClass = isShiny ? ' pokemon-link-shiny' : '';
        const shinyTitle = isShiny ? ' title="Shiny!"' : '';
        const fallbackColor = formId > 0
          ? (isShiny ? '#ff7a00' : '#ff9b3f')
          : (isShiny ? '#ffcb05' : (color ? `#${color}` : '#00d0ff'));
        const rainbowClass = isShiny ? ' pokemon-link-rainbow' : '';
        return `<span class="pokemon-link${rainbowClass}${formClass}${shinyClass}" style="color: ${fallbackColor};" data-stats="${statsStr}" data-name="${name}"${shinyTitle}>&lt;${name}&gt;</span>`;
      });

      // 2. Resolve Standard Color Tags: [HEX]text[-]
      html = html.replace(/\[([A-Fa-f0-9]{6})\](.*?)(?=\[([A-Fa-f0-9]{6})\]|\[-\]|$)/g, (match, hex, text) => {
        return `<span style="color: #${hex}">${text}</span>`;
      });

      html = html.replace(/\[-\]/g, '');

      return html;
    },
    createDatasource() {
      const self = this;
      return {
        getRows(params) {
          self.loading = true;
          let req = params.request;
          req.server = self.activeServer;
          self.$socket.sendObj({ event: 'searchChatlog', data: JSON.stringify(req) });
          self.cb = params.successCallback;
        }
      };
    }
    ,
    resolveFormLabel(statsStr) {
      const s = statsStr.split(',');
      if (s.length < 20) return '';
      const formId = Number(s[19]);
      if (!formId) return '';
      const monsterId = Number(s[0]);
      const mapping = (this.formMapping && this.formMapping.mappings) || {};
      const forms = mapping[monsterId];
      if (!forms) return '';
      const match = forms.find(f => Number(f.id) === formId);
      if (!match) return '';
      const duplicates = forms.filter(f => f.name === match.name);
      if (duplicates.length > 1) {
        const idx = duplicates.findIndex(f => Number(f.id) === formId) + 1;
        return `${match.name} (${idx})`;
      }
      return match.name;
    },
    escapeHtml(text) {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  },
  created() {
    this.gridOptions = Object.assign({}, DefaultServerGridOptions, {
      rowModelType: 'serverSide',
      columnDefs: this.createColumnDefs(),
      rowHeight: 38,
      headerHeight: 40,
      pagination: true,
      paginationPageSize: 100,
      cacheBlockSize: 100,
      animateRows: true,
      onCellMouseOver: params => this.handleCellMouseOver(params),
      onCellMouseOut: () => this.handleCellMouseOut(),
      getContextMenuItems: () => ['copy', 'separator', 'export']
    });
    
    applyGridPersistence(this.gridOptions, 'chat-log-grid-v3');
    if (!this.formMapping || !Object.keys(this.formMapping).length) {
      this.emit('getFormMapping', '');
    }
  },
  mounted() {
    const container = this.$refs.gridContainer;
    if (container) {
      this._gridMouseMove = event => this.handleGridMouseMove(event);
      this._gridMouseLeave = () => this.handleGridMouseLeave();
      container.addEventListener('mousemove', this._gridMouseMove);
      container.addEventListener('mouseleave', this._gridMouseLeave);
    }
  },
  beforeDestroy() {
    const container = this.$refs.gridContainer;
    if (container && this._gridMouseMove && this._gridMouseLeave) {
      container.removeEventListener('mousemove', this._gridMouseMove);
      container.removeEventListener('mouseleave', this._gridMouseLeave);
    }
    if (this.messageQuickFilterTimer) {
      clearTimeout(this.messageQuickFilterTimer);
      this.messageQuickFilterTimer = null;
    }
  },
  watch: {
    chatlogs(val) {
      this.loading = false;
      if (this.cb && val) {
        this.cb(val.rows, val.lastRow);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.custom-chat-grid {
  border: none;
  background-color: #1f2428;

  &::v-deep {
    .ag-header {
      background-color: #23282c !important;
      border-bottom: 1px solid #181b1e;
    }
    .ag-row {
      border-bottom: 1px solid #23282c;
      background-color: #1f2428;
      transition: background-color 0.15s;
      &:hover {
        background-color: rgba(255, 255, 255, 0.03) !important;
      }
    }
    .ag-cell {
      display: flex;
      align-items: center;
      line-height: 1.5;
      padding-top: 2px;
      padding-bottom: 2px;
      color: #f0f3f5;
      font-size: 13px;
      .text-info {
        font-size: 13px;
      }
      .text-muted {
        font-size: 13px;
      }
      small {
        font-size: 12px;
      }
    }
    .ag-paging-panel {
        background-color: #23282c;
        color: #f0f3f5;
        border-top: 1px solid #181b1e;
    }
  }
}

.border-bottom-dark {
    border-bottom: 1px solid #181b1e;
}

.chat-type-tabs {
  background: linear-gradient(120deg, rgba(34, 42, 48, 0.96), rgba(24, 28, 32, 0.96));
  padding: 10px 16px;
}

.chat-type-tabs-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.chat-type-tabs-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #7e8a97;
  font-weight: 700;
}

.chat-type-tabs-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chat-type-tabs-search {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(20, 26, 30, 0.92);
  color: #d6dee6;
}

.chat-type-tabs-search i {
  font-size: 12px;
  color: #8fb8d0;
}

.chat-type-tabs-input {
  background: transparent;
  border: none;
  outline: none;
  color: #e7edf3;
  font-size: 13px;
  min-width: 240px;
}

.chat-type-tabs-input::placeholder {
  color: rgba(231, 237, 243, 0.55);
}

.chat-type-tab {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(20, 26, 30, 0.92);
  color: #d6dee6;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: transform 0.12s ease, background 0.12s ease, color 0.12s ease, box-shadow 0.12s ease;
}

.chat-type-tab i {
  font-size: 11px;
}

.chat-type-tab:hover {
  transform: translateY(-1px);
  color: #ffffff;
  background: rgba(46, 170, 220, 0.18);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.35);
}

.chat-type-tab.active {
  background: linear-gradient(135deg, rgba(54, 196, 150, 0.55), rgba(60, 140, 220, 0.45));
  color: #0b1a1f;
  box-shadow: 0 6px 18px rgba(54, 196, 150, 0.35);
  border-color: rgba(54, 196, 150, 0.45);
}

.chat-type-tab-all {
  border-color: rgba(32, 168, 216, 0.4);
}

.chat-type-tab-all.active {
  background: linear-gradient(135deg, rgba(32, 168, 216, 0.6), rgba(46, 128, 196, 0.5));
  color: #0b1a1f;
  box-shadow: 0 6px 18px rgba(32, 168, 216, 0.35);
}

.chat-type-tab-trade {
  border-color: rgba(242, 153, 74, 0.5);
}

.chat-type-tab-trade.active {
  background: linear-gradient(135deg, rgba(242, 153, 74, 0.7), rgba(239, 128, 54, 0.6));
  color: #201a14;
  box-shadow: 0 6px 18px rgba(242, 153, 74, 0.35);
}

.chat-type-tab-battle {
  border-color: rgba(216, 74, 74, 0.5);
}

.chat-type-tab-battle.active {
  background: linear-gradient(135deg, rgba(216, 74, 74, 0.75), rgba(195, 55, 55, 0.65));
  color: #1a0b0b;
  box-shadow: 0 6px 18px rgba(216, 74, 74, 0.35);
}

.chat-type-tab-help {
  border-color: rgba(47, 128, 237, 0.5);
}

.chat-type-tab-help.active {
  background: linear-gradient(135deg, rgba(47, 128, 237, 0.7), rgba(35, 106, 207, 0.6));
  color: #0b1220;
  box-shadow: 0 6px 18px rgba(47, 128, 237, 0.35);
}

.chat-type-tab-other {
  border-color: rgba(108, 92, 231, 0.5);
}

.chat-type-tab-other.active {
  background: linear-gradient(135deg, rgba(108, 92, 231, 0.75), rgba(88, 72, 211, 0.6));
  color: #110d2a;
  box-shadow: 0 6px 18px rgba(108, 92, 231, 0.35);
}


.chat-message-text {
  word-break: break-word;
  white-space: pre-wrap;
  color: #e4e7ea;
  font-size: 13px;
}

.chat-user-link {
  font-weight: 600;
  text-decoration: none;
}

.chat-user-link:hover {
  text-decoration: underline;
}

:deep(.pokemon-link) {
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0 2px;

  &:hover {
    filter: brightness(1.2);
    text-shadow: 0 0 12px currentColor;
    border-radius: 2px;
  }
}

:deep(.pokemon-link-rainbow) {
  display: inline-block;
}

:deep(.pokemon-link-form) {
  color: #ff9b3f;
}

:deep(.pokemon-link-shiny) {
}

:deep(.pokemon-link-form.pokemon-link-shiny) {
  color: #ff7a00;
}

@keyframes pokemon-rainbow {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}

@supports (-webkit-background-clip: text) {
  :deep(.pokemon-link-rainbow) {
    background-image: linear-gradient(90deg, #ff4d4d, #ffb84d, #fff24d, #7dff4d, #4dd2ff, #7f6dff, #ff4df3, #ff4d4d);
    background-size: 200% 100%;
    animation: pokemon-rainbow 3s linear infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  :deep(.pokemon-link-rainbow) {
    animation: none;
    background-image: none;
    color: inherit !important;
    -webkit-text-fill-color: currentColor;
  }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10;
  backdrop-filter: blur(2px);
}

.bg-primary-soft-dark {
  background-color: rgba(32, 168, 216, 0.15);
}

.badge {
  font-weight: 600;
  letter-spacing: 0.5px;
  border: 1px solid transparent;
}

.badge-dark {
    background-color: #23282c;
    color: #f0f3f5;
}

.floating-pokemon-card-wrapper {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  transition: opacity 0.1s ease-out;
  filter: drop-shadow(0 10px 25px rgba(0,0,0,0.5));
}
</style>
